import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import {
  placeScheduleWithoutVisitOnly,
  updateScheduleData
} from "../../Schedule/functions/handleScheduleData.functions";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import { ScheduleMode } from "../../Schedule/partial/ScheduleModeToggle.partial";
import type { ScheduleResourceAutocompleteOption } from "../../Schedule/partial/ScheduleResourceAutocomplete.partial";
import { useRemoteFetchNoPlaceZoneBookingSchedule } from "../../../hooks/Remote/Schedule/useRemoteFetchNoPlaceZoneBookingSchedule";
import { useRemoteFetchPlaceSchedule } from "../../../hooks/Remote/Schedule/useRemoteFetchPlaceSchedule";
import { useRemoteFetchVehicleSchedule } from "../../../hooks/Remote/Schedule/useRemoteFetchVehicleSchedule";
import { useRemoteFetchVisitOnlySchedule } from "../../../hooks/Remote/Schedule/useRemoteFetchVisitOnlySchedule";
import { useRemoteResourceCalendarByBookingObjectMutation } from "../../../hooks/Remote/Schedule/useRemoteResourceCalendarByBookingObjectMutation";
import { selectVisitorManagementEnabled } from "../../Login/selectors/selectVisitorManagementEnabled";
import { getDashboard2WeekRange } from "../utils/dashboard2-schedule.utils";

type WeekScheduleOptions = {
  scheduleMode: ScheduleMode;
  selectedColleagues: TeamMember[];
  selectedResource: ScheduleResourceAutocompleteOption | null;
};

export function useDashboard2WeekSchedules(weekAnchor: DateTime, options: WeekScheduleOptions) {
  const { scheduleMode, selectedColleagues, selectedResource } = options;
  const {
    sub,
    placeTypes,
    zoneTypes,
    company: {
      meta: { workplaceBookingEnabled }
    }
  } = useSelector((state: RootState) => state.login.userInformation);

  const visitorManagementEnabled = useSelector(selectVisitorManagementEnabled);
  const hasVehicleLicense = placeTypes.some(type => type.name === BookingType.VEHICLE);
  const hasConferenceLicense = zoneTypes.some(type => type.name === BookingType.CONFERENCEZONE);
  const isColleagueMode = scheduleMode === ScheduleMode.Colleague;

  const { startDate, endDate, workDays } = useMemo(
    () => getDashboard2WeekRange(weekAnchor),
    [weekAnchor]
  );

  const [schedules, setSchedules] = useState<BookingScheduleInterface[]>([]);
  const [resourceSchedules, setResourceSchedules] = useState<BookingScheduleInterface[]>([]);
  const [resourceScheduleLoading, setResourceScheduleLoading] = useState(false);
  const [resourceScheduleFailed, setResourceScheduleFailed] = useState(false);

  const userIds = useMemo(
    () =>
      Array.from(
        new Set([
          sub,
          ...selectedColleagues
            .map(colleague => colleague.userId)
            .filter((userId): userId is string => userId !== undefined && userId.trim() !== "")
        ])
      ),
    [selectedColleagues, sub]
  );

  const bookingScheduleDto = useMemo(
    () => ({
      userIds,
      startDate,
      endDate
    }),
    [endDate, startDate, userIds]
  );

  const colleaguesReady = isColleagueMode && userIds.length > 0;
  const resourceReady = !isColleagueMode && selectedResource != null;

  const {
    data: placeScheduleData,
    isFetching: placeScheduleDataIsFetching,
    isError: placeScheduleDataIsError,
    refetch: refetchPlaceSchedule
  } = useRemoteFetchPlaceSchedule({
    bookingScheduleDto,
    workplaceBookingEnabled: workplaceBookingEnabled && colleaguesReady
  });

  const {
    data: visitOnlyScheduleData,
    isFetching: visitOnlyScheduleDataIsFetching,
    isError: visitOnlyScheduleDataIsError,
    refetch: refetchVisitOnlySchedule
  } = useRemoteFetchVisitOnlySchedule(
    bookingScheduleDto,
    visitorManagementEnabled && colleaguesReady
  );

  const {
    data: noPlaceZoneBookingScheduleData,
    isFetching: noPlaceZoneBookingScheduleDataIsFetching,
    isError: noPlaceZoneBookingScheduleDataIsError,
    refetch: refetchNoPlaceZoneBookingSchedule
  } = useRemoteFetchNoPlaceZoneBookingSchedule({
    bookingScheduleDto,
    hasNoPlaceZoneLicense: hasConferenceLicense && colleaguesReady
  });

  const {
    data: vehicleScheduleData,
    isFetching: vehicleScheduleDataIsFetching,
    isError: vehicleScheduleDataIsError,
    refetch: refetchVehicleSchedule
  } = useRemoteFetchVehicleSchedule({
    bookingScheduleDto,
    hasVehicleLicense: hasVehicleLicense && colleaguesReady
  });

  const { mutateAsync: fetchResourceCalendarByBookingObject } =
    useRemoteResourceCalendarByBookingObjectMutation();

  useEffect(() => {
    if (!isColleagueMode) {
      setSchedules([]);
      return;
    }

    updateScheduleData(
      placeScheduleData,
      visitorManagementEnabled ? visitOnlyScheduleData : undefined,
      noPlaceZoneBookingScheduleData,
      vehicleScheduleData,
      setSchedules
    );
  }, [
    isColleagueMode,
    noPlaceZoneBookingScheduleData,
    placeScheduleData,
    vehicleScheduleData,
    visitOnlyScheduleData,
    visitorManagementEnabled
  ]);

  useEffect(() => {
    if (isColleagueMode || !selectedResource) {
      setResourceSchedules([]);
      setResourceScheduleLoading(false);
      setResourceScheduleFailed(false);
      return;
    }

    let aborted = false;
    setResourceScheduleLoading(true);
    setResourceScheduleFailed(false);
    fetchResourceCalendarByBookingObject({
      bookingInventoryId: selectedResource.bookingInventoryId,
      bookingType: selectedResource.bookingType,
      startDate,
      endDate
    })
      .then(({ schedules: nextSchedules }) => {
        if (!aborted) setResourceSchedules(nextSchedules);
      })
      .catch(() => {
        if (!aborted) {
          setResourceSchedules([]);
          setResourceScheduleFailed(true);
        }
      })
      .finally(() => {
        if (!aborted) setResourceScheduleLoading(false);
      });

    return () => {
      aborted = true;
    };
  }, [endDate, fetchResourceCalendarByBookingObject, isColleagueMode, selectedResource, startDate]);

  const visibleSchedules = isColleagueMode ? schedules : resourceSchedules;

  const isLoading =
    (isColleagueMode &&
      (!colleaguesReady ||
        (workplaceBookingEnabled && placeScheduleDataIsFetching) ||
        (visitorManagementEnabled && visitOnlyScheduleDataIsFetching) ||
        (hasConferenceLicense && noPlaceZoneBookingScheduleDataIsFetching) ||
        (hasVehicleLicense && vehicleScheduleDataIsFetching))) ||
    (resourceReady && resourceScheduleLoading);

  const scheduleLoadFailed =
    (isColleagueMode &&
      colleaguesReady &&
      ((workplaceBookingEnabled && placeScheduleDataIsError) ||
        (visitorManagementEnabled && visitOnlyScheduleDataIsError) ||
        (hasConferenceLicense && noPlaceZoneBookingScheduleDataIsError) ||
        (hasVehicleLicense && vehicleScheduleDataIsError))) ||
    (resourceReady && resourceScheduleFailed);

  const refetchSchedules = useCallback(() => {
    if (isColleagueMode) {
      if (!colleaguesReady) {
        return;
      }

      if (workplaceBookingEnabled) {
        refetchPlaceSchedule();
      }
      if (hasConferenceLicense) {
        refetchNoPlaceZoneBookingSchedule();
      }
      if (hasVehicleLicense) {
        refetchVehicleSchedule();
      }
      if (visitorManagementEnabled) {
        refetchVisitOnlySchedule();
      }
      return;
    }

    if (!selectedResource) {
      return;
    }

    setResourceScheduleLoading(true);
    setResourceScheduleFailed(false);
    fetchResourceCalendarByBookingObject({
      bookingInventoryId: selectedResource.bookingInventoryId,
      bookingType: selectedResource.bookingType,
      startDate,
      endDate
    })
      .then(({ schedules: nextSchedules }) => {
        setResourceSchedules(nextSchedules);
      })
      .catch(() => {
        setResourceSchedules([]);
        setResourceScheduleFailed(true);
      })
      .finally(() => {
        setResourceScheduleLoading(false);
      });
  }, [
    colleaguesReady,
    endDate,
    fetchResourceCalendarByBookingObject,
    hasConferenceLicense,
    hasVehicleLicense,
    isColleagueMode,
    refetchNoPlaceZoneBookingSchedule,
    refetchPlaceSchedule,
    refetchVehicleSchedule,
    refetchVisitOnlySchedule,
    selectedResource,
    startDate,
    visitorManagementEnabled,
    workplaceBookingEnabled
  ]);

  return {
    schedules: visibleSchedules,
    workDays,
    isLoading,
    scheduleLoadFailed,
    refetchSchedules,
    placeScheduleCount: placeScheduleWithoutVisitOnly(placeScheduleData).length,
    currentUserId: sub
  };
}
